import { inject, Injectable, signal, Signal, WritableSignal } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { BaseWooCommerceAddressDetails } from '../../interfaces/woo-commerce/woo-commerce.interface';
import { HappyTenantContextService } from './happy-tenant-context.service';

export interface HappyStoreConfig {
  domain: string;
  name: string;
  tenant?: string;
  storeApiKey?: string;
  address: BaseWooCommerceAddressDetails;
}

@Injectable({ providedIn: 'root' })
export class HappyStoreSelectionService {
  private readonly STORAGE_KEY = 'happy_selected_store_index';
  private readonly API_STORE_KEY = 'happy_selected_api_store';
  private readonly tenantContext = inject(HappyTenantContextService);
  // Initialized eagerly from sessionStorage / URL path so that any consumer
  // that injects this service before `bootstrapSelectionContext()` runs
  // (e.g. NGXS state classes during environment-initializer phase) sees the
  // correct selected store and not the default `0` index.
  private readonly _selectedIndex: WritableSignal<number> = signal<number>(
    this.computeInitialIndex()
  );

  readonly selectedIndex: Signal<number> = this._selectedIndex.asReadonly();

  bootstrapSelectionContext(): void {
    const pathIndex = this.findIndexByDomain(this.getPathStoreHint());
    if (pathIndex >= 0) {
      this._selectedIndex.set(pathIndex);
      this.writeIndexToStorage(pathIndex);
      this.syncTenantContext();
      return;
    }

    this._selectedIndex.set(this.resolveInitialIndex());
    this.syncTenantContext();
  }

  private computeInitialIndex(): number {
    try {
      return this.resolveInitialIndex();
    } catch {
      return 0;
    }
  }

  getStores(): HappyStoreConfig[] {
    const stores = this.readStoresFromEnvironment();
    if (stores.length > 0) {
      return stores;
    }
    const singleStore = this.buildSingleStoreFallback();
    return singleStore ? [singleStore] : [];
  }

  getSelectedIndex(): number {
    const index = this._selectedIndex();
    const stores = this.getStores();
    if (stores.length === 0) {
      return 0;
    }
    if (index < 0 || index >= stores.length) {
      return 0;
    }
    return index;
  }

  getSelected(): HappyStoreConfig | null {
    const stores = this.getStores();
    if (stores.length === 0) {
      return null;
    }

    const selected = stores[this.getSelectedIndex()] || null;
    if (!selected) {
      return null;
    }

    const currentDomain = this.tenantContext.storeName();
    const currentApiKey = this.tenantContext.storeApiKey();
    if (
      currentDomain &&
      currentApiKey &&
      selected.domain.toLowerCase() === currentDomain.toLowerCase() &&
      !selected.storeApiKey
    ) {
      return { ...selected, storeApiKey: currentApiKey };
    }

    return selected;
  }

  getStore(index: number): HappyStoreConfig | null {
    const stores = this.getStores();
    if (index < 0 || index >= stores.length) {
      return null;
    }
    return stores[index] || null;
  }

  select(index: number): boolean {
    const stores = this.getStores();
    if (index < 0 || index >= stores.length) {
      return false;
    }

    const indexChanged = index !== this._selectedIndex();
    const storageHadEntry = this.hasStorageEntry();

    this._selectedIndex.set(index);
    this.writeIndexToStorage(index);
    this.syncTenantContext();

    if (!indexChanged && storageHadEntry) {
      return false;
    }

    return indexChanged;
  }

  findIndexByDomain(domain: string): number {
    if (!domain) {
      return -1;
    }
    const normalized = domain.toLowerCase();
    return this.getStores().findIndex(
      (store) => (store.domain || '').toLowerCase() === normalized
    );
  }

  selectByDomain(domain: string): boolean {
    const index = this.findIndexByDomain(domain);
    if (index < 0) {
      return false;
    }
    return this.select(index);
  }

  selectByDomainWithApiKey(domain: string, apiKey: string): void {
    const index = this.findIndexByDomain(domain);
    if (index >= 0) {
      this.select(index);
      this.clearApiStoreSelection();
      return;
    }

    this.writeApiStoreSelection(domain, apiKey);
    this.tenantContext.setSelectedStore({
      storeName: domain,
      storeApiKey: apiKey
    });
  }

  readApiStoreSelection(): { domain: string; apiKey: string } | null {
    try {
      const raw = sessionStorage.getItem(this.API_STORE_KEY);
      if (!raw) {
        return null;
      }
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  clearApiStoreSelection(): void {
    try {
      sessionStorage.removeItem(this.API_STORE_KEY);
    } catch {}
  }

  private writeApiStoreSelection(domain: string, apiKey: string): void {
    try {
      sessionStorage.setItem(
        this.API_STORE_KEY,
        JSON.stringify({ domain, apiKey })
      );
    } catch {}
  }

  hasExplicitSelection(): boolean {
    if (this.getStores().length <= 1) {
      return true;
    }
    if (this.hasStorageEntry()) {
      return true;
    }

    const pathHint = this.getPathStoreHint();
    return !!pathHint && this.findIndexByDomain(pathHint) >= 0;
  }

  hasExplicitUserSelection(): boolean {
    if (this.hasStorageEntry()) {
      return true;
    }
    if (this.readApiStoreSelection()) {
      return true;
    }
    const pathHint = this.getPathStoreHint();
    return !!pathHint && pathHint.length > 0;
  }

  clearSelection(): void {
    try {
      sessionStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.warn('Failed to clear Happy selected store index:', error);
    }
    this._selectedIndex.set(0);
    this.syncTenantContext();
  }

  private readStoresFromEnvironment(): HappyStoreConfig[] {
    const happyApi: any = (environment.happyApi as any) || {};
    const stores = Array.isArray(happyApi.stores) ? happyApi.stores : [];
    const tenant = String(happyApi.tenant || '').trim();

    return stores.map((store: HappyStoreConfig) => ({
      ...store,
      tenant: store.tenant || tenant
    }));
  }

  private buildSingleStoreFallback(): HappyStoreConfig | null {
    const happyApi: any = (environment as any).happyApi || {};
    const storeDomain = this.tenantContext.storeName() || '';
    const tenant = this.tenantContext.tenant() || happyApi.tenant || '';
    const storeApiKey = this.tenantContext.storeApiKey() || '';

    if (!storeDomain && !tenant && !storeApiKey) {
      return null;
    }

    const restaurant = (environment as any).restaurant || {};
    return {
      domain: storeDomain,
      name: restaurant.name || storeDomain,
      tenant,
      storeApiKey,
      address: this.getEmptyAddress()
    };
  }

  private resolveInitialIndex(): number {
    const storedIndex = this.readIndexFromStorage();
    const stores = this.getStores();
    if (stores.length === 0) {
      return 0;
    }
    if (storedIndex !== null && storedIndex >= 0 && storedIndex < stores.length) {
      return storedIndex;
    }

    const pathIndex = this.findIndexByDomain(this.getPathStoreHint());
    if (pathIndex >= 0) {
      return pathIndex;
    }

    return 0;
  }

  private syncTenantContext(): void {
    const selected = this.getSelected();
    if (!selected) {
      return;
    }

    this.tenantContext.setSelectedStore({
      tenant: selected.tenant,
      storeName: selected.domain,
      storeApiKey: selected.storeApiKey || null
    });
  }

  private getPathStoreHint(): string {
    if (typeof window === 'undefined') {
      return '';
    }
    try {
      const pathname = window.location?.pathname || '';
      const firstSegment = pathname.split('/').filter(Boolean)[0] || '';
      return decodeURIComponent(firstSegment).trim();
    } catch {
      return '';
    }
  }

  private hasStorageEntry(): boolean {
    try {
      return sessionStorage.getItem(this.STORAGE_KEY) !== null;
    } catch {
      return false;
    }
  }

  private readIndexFromStorage(): number | null {
    try {
      const raw =
        sessionStorage.getItem(this.STORAGE_KEY) ??
        localStorage.getItem(this.STORAGE_KEY);
      if (raw === null) {
        return null;
      }
      const parsed = Number(raw);
      if (!Number.isFinite(parsed) || parsed < 0) {
        return null;
      }
      return parsed;
    } catch (error) {
      console.warn('Failed to read Happy selected store index:', error);
      return null;
    }
  }

  private writeIndexToStorage(index: number): void {
    try {
      sessionStorage.setItem(this.STORAGE_KEY, String(index));
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.warn('Failed to save Happy selected store index:', error);
    }
  }

  private getEmptyAddress(): BaseWooCommerceAddressDetails {
    return {
      address_1: '',
      address_2: '',
      city: '',
      state: '',
      postcode: '',
      country: ''
    };
  }
}
